// src/pages/DriverMyTrips.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CSVLink } from "react-csv";
import DriverHeader from "../components/DriverHeader";
import DriverFooter from "../components/DriverFooter";
import TripReplayMap from "../components/TripReplayMap";
import bookingWS from "../services/bookingWebSocket";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";
import { fetchBookings } from "../actions/BookingActions";
import { fetchUsers } from "../actions/UserActions";
import { fetchVehicles } from "../actions/VehicleActions";

const DriverMyTrips = () => {
  const dispatch = useDispatch();
  const { authKey, userDetails } = useSelector((state) => state.auth);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchDriverTrips = useCallback(async () => {
    if (!authKey || !userDetails?.ServiceNo) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch bookings, users, and vehicles in parallel via Redux
      const [allBookings, usersData, vehiclesData] = await Promise.all([
        dispatch(fetchBookings()),
        dispatch(fetchUsers()),
        dispatch(fetchVehicles())
      ]);

      // Create a map of ServiceNo -> User for quick lookup
      const userMap = {};
      if (Array.isArray(usersData)) {
        usersData.forEach(user => {
          if (user.ServiceNo) {
            userMap[user.ServiceNo] = user;
          }
        });
      }

      // Create a map of vehicleId -> Vehicle for quick lookup
      const vehicleMap = {};
      if (Array.isArray(vehiclesData)) {
        vehiclesData.forEach(vehicle => {
          if (vehicle.vehicleId || vehicle.vId) {
            const key = vehicle.vehicleId || vehicle.vId;
            vehicleMap[key] = vehicle;
          }
        });
      }

      if (Array.isArray(allBookings)) {
        const driverTrips = allBookings
          .filter((b) => b.dSNo === userDetails.ServiceNo)
          .map((booking) => {
            // Get the employee who made this booking using bookingSno field
            const employeeServiceNo = booking.bookingSno || booking.BookingSno;
            const employee = userMap[employeeServiceNo];
            
            // Normalize status to uppercase
            const status = (booking.status || "").toString().trim().toUpperCase();
            
            // Status mapping based on actual backend codes:
            // P = Pending
            // A = Active  
            // C = Cancelled
            // CO = Completed
            // IP = In Progress
            // EN = Enroute
            const isPending = status === "P" || status === "PENDING";
            const isActiveTrip = status === "A" || status === "ACTIVE" || status === "EN" || status === "ENROUTE" || status === "IP" || status === "INPROGRESS" || status === "IN PROGRESS";
            const isCompleted = status === "CO" || status === "COMPLETED";
            const isCancelled = status === "C" || status === "CANCEL" || status === "CANCELLED";

            // === REAL DISTANCE FROM YOUR BACKEND ===
            const rawDistance = booking.totalDestence || booking.TotalDestence || booking.distance || null;
            const distance = rawDistance ? `${parseFloat(rawDistance).toFixed(1)} km` : "—";

            // === REAL DURATION FROM bsTime & beTime ===
            let duration = "—";
            if (booking.bsTime && booking.beTime) {
              const start = new Date(booking.bsTime);
              const end = new Date(booking.beTime);

              // Skip default 1900 dates
              if (
                !isNaN(start.getTime()) &&
                !isNaN(end.getTime()) &&
                start.getFullYear() > 1900 &&
                end.getFullYear() > 1900 &&
                end > start
              ) {
                const mins = Math.round((end - start) / 60000);
                duration = mins > 0 ? `${mins} min` : "—";
              }
            }

            return {
              ...booking,
              bId: booking.bId || booking.BId,
              employeeSNo: employeeServiceNo || "N/A",
              employeeName: employee?.Name || booking.EmployeeName || booking.employeeName || "Unknown",
              vId: booking.vId || "N/A",
              vehicleType: vehicleMap[booking.vId || booking.vId]?.vehicleType || vehicleMap[booking.vId || booking.vId]?.vehicle_type || "Vehicle",
              pickupLocation: booking.startLocation || `Lat ${parseFloat(booking.slatitude).toFixed(4)}, Lng ${parseFloat(booking.slongitude).toFixed(4)}`,
              dropoffLocation: booking.endLocation || `Lat ${parseFloat(booking.elatitude).toFixed(4)}, Lng ${parseFloat(booking.elongitude).toFixed(4)}`,
              description: booking.Description || booking.description || "-",
              bookingDate: booking.bookingDT || booking.bookingDate,

              status,
              statusLabel: isPending ? "Pending" 
                         : isActiveTrip ? "Active Trip" 
                         : isCompleted ? "Completed" 
                         : isCancelled ? "Cancelled"
                         : "Unknown",
              statusColor: isPending ? "bg-yellow-100 text-yellow-800"
                         : isActiveTrip ? "bg-green-100 text-green-800" 
                         : isCompleted ? "bg-blue-100 text-blue-800" 
                         : isCancelled ? "bg-red-100 text-red-800"
                         : "bg-gray-100 text-gray-800",

              date: new Date(booking.bookingDT || booking.bookingDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              }),
              time: new Date(booking.bookingDT || booking.bookingDate).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit"
              }),

              // FINAL WORKING VALUES
              distance,
              duration,

              startTime: booking.bsTime && !booking.bsTime.includes("1/1/1900") 
                ? new Date(booking.bsTime).toLocaleString("en-GB") 
                : "—",
              endTime: booking.beTime && !booking.beTime.includes("1/1/1900") 
                ? new Date(booking.beTime).toLocaleString("en-GB") 
                : "—",

              slatitude: parseFloat(booking.slatitude),
              slongitude: parseFloat(booking.slongitude),
              elatitude: parseFloat(booking.elatitude),
              elongitude: parseFloat(booking.elongitude),
            };
          })
          .sort((a, b) => new Date(b.bookingDate) - new Date(a.bookingDate));

        setTrips(driverTrips);
      }
    } catch (err) {
      toast.error("Failed to load your trips");
      console.error("Trip fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [authKey, userDetails?.ServiceNo]);

  useEffect(() => {
    fetchDriverTrips();
  }, [fetchDriverTrips]);

  // WebSocket for real-time status updates
  useEffect(() => {
    if (!authKey || !userDetails?.ServiceNo) return;

    // Connect to booking WebSocket
    bookingWS.connect(authKey);

    // Listen for status updates
    const handleStatusUpdate = (message) => {
      if (message && message.bId) {
        setTrips(prevTrips => {
          return prevTrips.map(trip => {
            if (trip.bId?.toString() === message.bId?.toString()) {
              // Normalize the new status
              const newStatus = (message.status || "").toString().trim().toUpperCase();
              
              // Determine the new status label and color
              const isPending = newStatus === "P" || newStatus === "PENDING";
              const isActiveTrip = newStatus === "A" || newStatus === "ACTIVE" || newStatus === "EN" || newStatus === "ENROUTE" || newStatus === "IP" || newStatus === "INPROGRESS" || newStatus === "IN PROGRESS";
              const isCompleted = newStatus === "CO" || newStatus === "COMPLETED";
              const isCancelled = newStatus === "C" || newStatus === "CANCEL" || newStatus === "CANCELLED";
              
              const statusLabel = isPending ? "Pending" 
                                : isActiveTrip ? "Active Trip" 
                                : isCompleted ? "Completed" 
                                : isCancelled ? "Cancelled"
                                : "Unknown";
              
              const statusColor = isPending ? "bg-yellow-100 text-yellow-800"
                                : isActiveTrip ? "bg-green-100 text-green-800" 
                                : isCompleted ? "bg-blue-100 text-blue-800" 
                                : isCancelled ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800";

              console.log(`[DriverMyTrips] Trip #${trip.bId} status updated: ${trip.status} → ${newStatus} (${statusLabel})`);
              
              // Show toast notification
              toast.info(`Trip #${trip.bId} status: ${statusLabel}`, { autoClose: 3000 });

              return {
                ...trip,
                status: newStatus,
                statusLabel,
                statusColor,
                ...message
              };
            }
            return trip;
          });
        });
      }
    };

    bookingWS.onStatusUpdate(handleStatusUpdate);

    return () => {
      bookingWS.disconnect();
    };
  }, [authKey, userDetails?.ServiceNo]);

  // Search & Filter
  const filteredTrips = useMemo(() => {
    return trips.filter((trip) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        trip.bId?.toString().includes(search) ||
        trip.employeeName?.toLowerCase().includes(search) ||
        trip.vId?.toLowerCase().includes(search) ||
        trip.pickupLocation?.toLowerCase().includes(search) ||
        trip.dropoffLocation?.toLowerCase().includes(search);

      // Filter by status category
      let matchesFilter = false;
      if (filterStatus === "ALL") {
        matchesFilter = true; // Show all trips
      } else if (filterStatus === "P") {
        // Pending trips
        matchesFilter = trip.status === "P" || trip.status === "PENDING";
      } else if (filterStatus === "ACTIVE") {
        // Active trips (A, EN, IP)
        matchesFilter = trip.status === "A" || trip.status === "ACTIVE" || trip.status === "EN" || trip.status === "ENROUTE" || trip.status === "IP" || trip.status === "INPROGRESS" || trip.status === "IN PROGRESS";
      } else if (filterStatus === "CO") {
        // Completed trips
        matchesFilter = trip.status === "CO" || trip.status === "COMPLETED";
      } else if (filterStatus === "C") {
        // Cancelled trips
        matchesFilter = trip.status === "C" || trip.status === "CANCEL" || trip.status === "CANCELLED";
      }

      return matchesSearch && matchesFilter;
    });
  }, [trips, searchTerm, filterStatus]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredTrips.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTrips = filteredTrips.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPrevPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // Stats
  const stats = useMemo(() => {
    const pending = trips.filter(t => t.status === "P" || t.status === "PENDING").length;
    const active = trips.filter(t => t.status === "A" || t.status === "ACTIVE" || t.status === "EN" || t.status === "ENROUTE" || t.status === "IP" || t.status === "INPROGRESS" || t.status === "IN PROGRESS").length;
    const completed = trips.filter(t => t.status === "CO" || t.status === "COMPLETED").length;
    const cancelled = trips.filter(t => t.status === "C" || t.status === "CANCEL" || t.status === "CANCELLED").length;
    const totalDistance = trips.reduce((sum, t) => sum + (parseFloat(t.distance) || 0), 0).toFixed(1);
    const totalMins = trips.reduce((sum, t) => sum + (parseInt(t.duration) || 0), 0);
    const totalHours = totalMins >= 60 
      ? `${Math.floor(totalMins / 60)}h ${totalMins % 60}m` 
      : totalMins > 0 ? `${totalMins}m` : "0m";

    return {
      total: trips.length,
      pending,
      active,
      completed,
      cancelled,
      totalDistance,
      totalHours
    };
  }, [trips]);

  const csvData = filteredTrips.map(t => ({
    "Trip ID": t.bId,
    "Vehicle": t.vId,
    "Employee": t.employeeName,
    "Pickup": t.pickupLocation,
    "Dropoff": t.dropoffLocation,
    "Date": t.date,
    "Time": t.time,
    "Duration": t.duration,
    "Distance": t.distance,
    "Status": t.statusLabel
  }));

  const openModal = useCallback((trip) => {
    setSelectedTrip(trip);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedTrip(null);
  }, []);

  if (!authKey) {
    return <div className="text-center py-20 text-2xl">Please login</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-100">
      <DriverHeader />

      <main className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 space-y-4 sm:space-y-6 md:space-y-8 pb-20 sm:pb-24">

        {/* Stats Grid - Mobile Optimized */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-1.5 sm:gap-2 md:gap-3">
          {[
            { label: "Total Trips", value: stats.total, color: "from-blue-500 to-indigo-600" },
            { label: "Pending", value: stats.pending, color: "from-yellow-500 to-amber-600" },
            { label: "Active", value: stats.active, color: "from-emerald-500 to-teal-600" },
            { label: "Completed", value: stats.completed, color: "from-cyan-500 to-blue-600" },
            { label: "Cancelled", value: stats.cancelled, color: "from-rose-500 to-pink-600" },
            { label: "Distance", value: `${stats.totalDistance} km`, color: "from-purple-500 to-violet-600" },
            { label: "Drive Time", value: stats.totalHours, color: "from-orange-500 to-red-600" },
          ].map((stat, i) => (
            <div key={i} className={`bg-gradient-to-br ${stat.color} text-white p-2 sm:p-3 md:p-4 lg:p-6 rounded-lg sm:rounded-xl md:rounded-2xl shadow-md sm:shadow-lg md:shadow-xl transform hover:scale-102 sm:hover:scale-105 transition-all`}>
              <p className="text-xs opacity-90">{stat.label}</p>
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mt-0.5 sm:mt-1 md:mt-2">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Search + Filter + Export - Mobile Optimized */}
        <div className="bg-white/90 backdrop-blur rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-3 sm:p-4 md:p-6 flex flex-col gap-3 sm:gap-4">
          <div className="relative">
            <label className="block text-sm sm:text-base font-bold text-indigo-800 mb-2">
              🔍 Search My Trips
            </label>
            <div className="relative flex items-center">
              <svg className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by ID, employee, vehicle, or location..."
                value={searchTerm}
                className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 rounded-lg sm:rounded-xl border-2 border-indigo-200 text-sm sm:text-base focus:ring-4 focus:ring-indigo-300 focus:border-indigo-500 transition bg-indigo-50/50 placeholder-gray-500"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex-1 sm:flex-initial px-3 sm:px-5 py-2 sm:py-3 rounded-lg sm:rounded-xl border border-gray-300 text-xs sm:text-sm focus:ring-4 focus:ring-indigo-300"
            >
              <option value="ALL">All Trips</option>
              <option value="P">Pending</option>
              <option value="ACTIVE">Active Trip</option>
              <option value="CO">Completed</option>
              <option value="C">Cancelled</option>
            </select>

            <CSVLink
              data={csvData}
              filename={`my-trips-${new Date().toISOString().split("T")[0]}.csv`}
              className={`flex-1 sm:flex-initial px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm shadow-lg flex items-center justify-center gap-2 transition transform ${
                filteredTrips.length === 0
                  ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                  : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white hover:scale-105"
              }`}
              onClick={(e) => {
                if (filteredTrips.length === 0) {
                  e.preventDefault();
                  toast.warn("No trips to export! Please adjust your filters.", { autoClose: 3000 });
                }
              }}
            >
              📊 Export CSV
            </CSVLink>
          </div>
        </div>

        {/* Trips List */}
        <div className="bg-white rounded-xl sm:rounded-3xl shadow-lg sm:shadow-2xl overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-100 flex justify-between items-center">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-indigo-900">My Trips History</h1>
            <IconButton
              onClick={fetchDriverTrips}
              disabled={loading}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
                '&.Mui-disabled': {
                  color: 'grey.400',
                }
              }}
              title="Refresh trips"
            >
              <RefreshIcon />
            </IconButton>
          </div>

          {loading ? (
            <div className="p-8 sm:p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-10 sm:h-12 w-10 sm:w-12 border-4 border-indigo-600 border-t-transparent"></div>
              <p className="mt-3 sm:mt-4 text-base sm:text-xl text-gray-600">Loading your trips...</p>
            </div>
          ) : filteredTrips.length === 0 ? (
            <div className="p-8 sm:p-20 text-center">
              <p className="text-4xl sm:text-8xl mb-3 sm:mb-6">📭</p>
              <p className="text-lg sm:text-2xl text-gray-500">No trips match your filters</p>
            </div>
          ) : (
            <>
            <div style={{ maxHeight: 'calc(5 * 60px + 24px)', overflowY: 'auto', overflowX: 'auto', paddingRight: '8px' }} className="scrollbar-thin scrollbar-thumb-indigo-400 scrollbar-track-gray-100">
              <style>{`
                .scrollbar-thin::-webkit-scrollbar {
                  height: 6px;
                  width: 6px;
                }
                .scrollbar-thin::-webkit-scrollbar-track {
                  background: rgb(243, 244, 246);
                  border-radius: 10px;
                }
                .scrollbar-thin::-webkit-scrollbar-thumb {
                  background: rgb(129, 140, 248);
                  border-radius: 10px;
                }
                .scrollbar-thin::-webkit-scrollbar-thumb:hover {
                  background: rgb(102, 126, 234);
                }
              `}</style>
              <table className="w-full text-sm min-w-max">
                <thead className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white sticky top-0">
                  <tr className="text-xs sm:text-sm">
                    <th className="px-3 sm:px-6 py-3 sm:py-5 text-left font-bold whitespace-nowrap">Employee</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-5 text-left font-bold whitespace-nowrap">Vehicle</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-5 text-left font-bold whitespace-nowrap">Status</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-5 text-left font-bold whitespace-nowrap">Time</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-5 text-left font-bold whitespace-nowrap">ID</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-5 text-left font-bold whitespace-nowrap">Route</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-5 text-left font-bold whitespace-nowrap">Duration</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-5 text-left font-bold whitespace-nowrap">Distance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-xs sm:text-sm">
                  {paginatedTrips.map((trip) => (
                    <tr
                      key={trip.bId}
                      onClick={() => openModal(trip)}
                      className="hover:bg-indigo-50 cursor-pointer transition-all duration-200"
                    >
                      <td className="px-3 sm:px-6 py-3 sm:py-5 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{trip.employeeName}</div>
                        {trip.employeeSNo && trip.employeeSNo !== "N/A" && (
                          <div className="text-xs text-gray-500">ID: {trip.employeeSNo}</div>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-5 text-gray-700 font-medium whitespace-nowrap">{trip.vId}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-5 whitespace-nowrap">
                        <span className={`inline-flex px-2 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold ${trip.statusColor}`}>
                          {trip.statusLabel}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-5 text-xs whitespace-nowrap">
                        <div>{trip.date}</div>
                        <div className="text-gray-500">{trip.time}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-5 font-bold text-indigo-700 whitespace-nowrap">#{trip.bId}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-5 text-xs whitespace-nowrap">
                        <div className="text-green-700 font-medium truncate max-w-xs">{trip.pickupLocation}</div>
                        <div className="text-red-700 truncate max-w-xs">to {trip.dropoffLocation}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-5 font-medium text-emerald-700 whitespace-nowrap">{trip.duration}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-5 font-medium text-purple-700 whitespace-nowrap">{trip.distance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {filteredTrips.length > 0 && (
              <div className="bg-white border-t border-gray-200 px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 rounded-b-2xl">
                {/* Items per page selector */}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span>per page</span>
                </div>

                {/* Page info */}
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-indigo-600">{startIndex + 1}</span> to{" "}
                  <span className="font-semibold text-indigo-600">{Math.min(endIndex, filteredTrips.length)}</span> of{" "}
                  <span className="font-semibold text-indigo-600">{filteredTrips.length}</span> trips
                </div>

                {/* Page navigation */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ««
                  </button>
                  <button
                    onClick={goToPrevPage}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ‹ Prev
                  </button>
                  
                  {/* Page numbers */}
                  <div className="hidden sm:flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Show first, last, current, and pages around current
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, index, arr) => (
                        <React.Fragment key={page}>
                          {index > 0 && arr[index - 1] !== page - 1 && (
                            <span className="px-2 text-gray-400">...</span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                              currentPage === page
                                ? "bg-indigo-600 text-white border-indigo-600"
                                : "border-gray-300 hover:bg-gray-100"
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                  </div>

                  {/* Mobile page indicator */}
                  <span className="sm:hidden px-3 py-1 text-sm text-gray-600">
                    {currentPage} / {totalPages}
                  </span>

                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next ›
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    »»
                  </button>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      </main>

      {/* Professional Trip Details Modal - Mobile Responsive */}
      {isModalOpen && selectedTrip && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50" onClick={closeModal}>
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white p-4 sm:p-6 rounded-t-2xl sm:rounded-t-3xl relative overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iYSIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVHJhbnNmb3JtPSJyb3RhdGUoNDUpIj48cGF0aCBkPSJNLTEwIDMwaDYwdjJoLTYweiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNhKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')] opacity-50"></div>
              <div className="relative flex justify-between items-start gap-3">
                <div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="bg-white/20 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">Trip ID</span>
                    <h2 className="text-2xl sm:text-3xl font-bold">#{selectedTrip.bId}</h2>
                  </div>
                  <p className="text-sm sm:text-lg opacity-90 mt-2 flex items-center gap-2 flex-wrap">
                    <span>📅</span> {selectedTrip.date} <span className="opacity-50 hidden sm:inline">•</span> <span>🕐</span> {selectedTrip.time}
                  </p>
                </div>
                <button 
                  onClick={closeModal} 
                  className="p-2 hover:bg-white/20 rounded-full transition-all duration-200 group flex-shrink-0"
                >
                  <svg className="w-6 h-6 group-hover:rotate-90 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Info Cards Row */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-4 rounded-lg sm:rounded-2xl border border-blue-200">
                  <div className="flex items-center gap-2 text-blue-600 mb-1">
                    <span>👤</span>
                    <p className="text-xs font-semibold uppercase tracking-wide">Employee</p>
                  </div>
                  <p className="text-sm sm:text-lg font-bold text-blue-900">{selectedTrip.employeeName}</p>
                  {selectedTrip.employeeSNo && selectedTrip.employeeSNo !== "N/A" && (
                    <p className="text-xs text-blue-600 mt-1">ID: {selectedTrip.employeeSNo}</p>
                  )}
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-violet-100 p-3 sm:p-4 rounded-lg sm:rounded-2xl border border-purple-200">
                  <div className="flex items-center gap-2 text-purple-600 mb-1">
                    <span>🚗</span>
                    <p className="text-xs font-semibold uppercase tracking-wide">Vehicle</p>
                  </div>
                  <p className="text-sm sm:text-lg font-bold text-purple-900">{selectedTrip.vId}</p>
                </div>
                
                <div className="bg-gradient-to-br from-emerald-50 to-green-100 p-3 sm:p-4 rounded-lg sm:rounded-2xl border border-emerald-200">
                  <div className="flex items-center gap-2 text-emerald-600 mb-1">
                    <span>⏱️</span>
                    <p className="text-xs font-semibold uppercase tracking-wide">Duration</p>
                  </div>
                  <p className="text-lg sm:text-2xl font-bold text-emerald-900">{selectedTrip.duration}</p>
                </div>
                
                <div className="bg-gradient-to-br from-orange-50 to-amber-100 p-3 sm:p-4 rounded-lg sm:rounded-2xl border border-orange-200">
                  <div className="flex items-center gap-2 text-orange-600 mb-1">
                    <span>📏</span>
                    <p className="text-xs font-semibold uppercase tracking-wide">Distance</p>
                  </div>
                  <p className="text-lg sm:text-2xl font-bold text-orange-900">{selectedTrip.distance}</p>
                </div>
              </div>

              {/* Locations */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-3 sm:p-4 rounded-lg sm:rounded-2xl shadow-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center text-sm sm:text-lg">
                      📍
                    </div>
                    <p className="font-semibold text-xs sm:text-sm">PICKUP</p>
                  </div>
                  <p className="text-xs sm:text-sm opacity-90 font-mono bg-black/10 px-2 sm:px-3 py-2 rounded-lg break-all">
                    {selectedTrip.pickupLocation}
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-red-500 to-rose-600 text-white p-3 sm:p-4 rounded-lg sm:rounded-2xl shadow-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center text-sm sm:text-lg">
                      🎯
                    </div>
                    <p className="font-semibold text-xs sm:text-sm">DESTINATION</p>
                  </div>
                  <p className="text-xs sm:text-sm opacity-90 font-mono bg-black/10 px-2 sm:px-3 py-2 rounded-lg break-all">
                    {selectedTrip.dropoffLocation}
                  </p>
                </div>
              </div>

              {/* Status & Times */}
              <div className="flex flex-wrap items-center justify-between gap-4 bg-gray-50 p-4 rounded-2xl">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                  <span className={`inline-flex px-4 py-2 rounded-full text-sm font-bold ${selectedTrip.statusColor}`}>
                    {selectedTrip.statusLabel}
                  </span>
                </div>
                {selectedTrip.startTime !== "—" && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Started</p>
                    <p className="font-semibold text-gray-800">{selectedTrip.startTime}</p>
                  </div>
                )}
                {selectedTrip.endTime !== "—" && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Ended</p>
                    <p className="font-semibold text-gray-800">{selectedTrip.endTime}</p>
                  </div>
                )}
              </div>

              {/* Note */}
              {selectedTrip.description && selectedTrip.description !== "-" && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-xl">
                  <p className="text-xs text-yellow-700 uppercase tracking-wide font-semibold mb-1">📝 Trip Note</p>
                  <p className="text-gray-800 italic">"{selectedTrip.description}"</p>
                </div>
              )}

              {/* Map Section */}
              <div className="bg-gray-100 rounded-2xl p-4">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>🗺️</span> Trip Route Map
                </h3>
                <div className="rounded-2xl overflow-hidden shadow-xl border-2 border-gray-200">
                  <TripReplayMap
                    pickup={[selectedTrip.slatitude, selectedTrip.slongitude]}
                    dropoff={[selectedTrip.elatitude, selectedTrip.elongitude]}
                    pickupLabel={selectedTrip.pickupLocation}
                    dropoffLabel={selectedTrip.dropoffLocation}
                    vehicleType={selectedTrip.vehicleType}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <DriverFooter />
    </div>
  );
};

export default DriverMyTrips;
